import React from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import './CustomIntlTelInput.module.css';
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js';
import {
  COUNTRY_CODE,
  ISO2,
  VALID_NUMBER_LENGTH,
  VALID_NUMBER_REGEX
} from '../../constants/constants';

// Types
interface CountryData {
  dialCode: string;
  iso2: string;
}

type CustomValidationFunction = (value: string, selectedCountryData: CountryData) => boolean;

interface CustomIntlTelInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onPhoneNumberChange?: (
    valid: boolean,
    phone: string,
    selectedCountryData: CountryData,
    fullNumber: string,
    extension: string
  ) => void;
  onPhoneNumberBlur?: () => void;
  customValidation?: CustomValidationFunction;
  containerClassName?: string;
  inputClassName?: string;
  style?: React.CSSProperties;
  formatOnInit?: boolean;
  placeholder?: string;
  isRequired?: boolean;
  id?: string;
}

/**
 * Validates a mobile number.
 * This will allow us to override the default validation to make sure the number is valid even when marked as invalid sometimes.
 * 
 * @param {string} value - The phone number to validate.
 * @param {CountryData} selectedCountryData - The selected country data.
 * @returns {boolean} True if the number is valid, false otherwise.
 */
export const isMobileNumberValid = (value: string, selectedCountryData: CountryData): boolean => {

  // (default is South Africa validation unless overridden by environment variables)
  if (!value.startsWith(COUNTRY_CODE) || 
      selectedCountryData.iso2 !== ISO2 ||
      value.length !== VALID_NUMBER_LENGTH) {
    return false;
  }

  const phoneNumber = parsePhoneNumber(value, selectedCountryData.iso2 as CountryCode);
  if (phoneNumber) {
    return phoneNumber.isValid();
  }

  return VALID_NUMBER_REGEX.test(value.slice(1));
};

const parsePhoneNumberSafely = (fullNumber: string, countryCode?: string): boolean => {
  if (countryCode) {
    try {
      return parsePhoneNumber(fullNumber, countryCode.toUpperCase() as CountryCode)?.isValid() || false;
    } catch {
      // fall through to retry without country code
    }
  }
  try {
    return parsePhoneNumber(fullNumber)?.isValid() || false;
  } catch {
    return false;
  }
};

/**
 * A custom PhoneInput component with additional validation capabilities.
 */
const CustomIntlTelInput: React.FC<CustomIntlTelInputProps> = ({
  customValidation,
  onPhoneNumberChange,
  onPhoneNumberBlur,
  containerClassName,
  inputClassName,
  style,
  value,
  onChange,
  isRequired = true,
  id,
  ...restProps
}) => {
  const handlePhoneNumberChange = React.useCallback(
    (newValue: string, countryInfo: any) => {
      if (onChange) {
        onChange(newValue);
      }

      if (onPhoneNumberChange) {
        const fullNumber = `+${newValue}`;

        const dialCode = countryInfo.dialCode || '27';
        const cleanValue = newValue ? newValue.replace(/\D/g, '') : '';
        const isEmptyNumber = !newValue || cleanValue === '' || cleanValue === dialCode;

        let isValid = false;

        if (isEmptyNumber && !isRequired) {
          isValid = true;
        } else if (!isEmptyNumber) {
          isValid = parsePhoneNumberSafely(fullNumber, countryInfo.countryCode);
        }

        const countryData: CountryData = {
          dialCode: countryInfo.dialCode || '27',
          iso2: countryInfo.countryCode?.toLowerCase() || 'za'
        };

        let customIsValid = isValid;
        if (!isValid && customValidation) {
          customIsValid = customValidation(fullNumber, countryData);
        }

        const cleanPhoneNumber = fullNumber.replace(/\D/g, '');

        onPhoneNumberChange(
          customIsValid,
          cleanPhoneNumber,
          countryData,
          fullNumber,
          ''
        );
      }
    },
    [customValidation, onPhoneNumberChange, onChange, isRequired]
  );

  return (
    <div className={containerClassName} style={{...style, position: 'relative'}}>
      <PhoneInput
        {...restProps}
        inputProps={id ? { id } : undefined}
        country="za"
        preferredCountries={['za', 'gb', 'zw']}
        value={value}
        onChange={handlePhoneNumberChange}
        onBlur={onPhoneNumberBlur}
        inputClass=""
        containerClass="w-100"
        buttonClass=""
        inputStyle={{
          width: '100%',
          height: '38px',
          paddingLeft: '48px',
          border: '1px solid #ced4da',
          borderLeft: 'none',
          borderRadius: '0 0.25rem 0.25rem 0',
          fontSize: '1rem',
          lineHeight: '1.5',
          color: '#495057',
          backgroundColor: '#fff',
          paddingTop: '0.375rem',
          paddingBottom: '0.375rem',
          boxShadow: 'none',
          outline: 'none'
        }}
        containerStyle={{ width: '100%', height: '38px', position: 'relative' }}
        buttonStyle={{
          border: '1px solid #ced4da',
          borderRight: 'none',
          borderRadius: '0.25rem 0 0 0.25rem',
          height: '38px',
          backgroundColor: '#fff',
          padding: '0',
          margin: '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '46px',
          width: '46px'
        }}
      />
    </div>
  );
};

export default CustomIntlTelInput;